import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  TextField,
  Alert,
  Checkbox,
  FormGroup,
  MenuItem,
  Select,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { BASE_URL } from "../services/configUrls";

// ── Axios instance ────────────────────────────────────────
const api = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("accessToken");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Constants (outside component) ────────────────────────
const ALLOWED_ROLES = ["spoc", "leaders", "dspoc", "tpo"];

// ── Styles ────────────────────────────────────────────────
const optionSx = (selected) => ({
  m: 0,
  px: 1.5,
  py: 1,
  borderRadius: "10px",
  border: `1px solid ${selected ? "#059669" : "#e5e7eb"}`,
  bgcolor: selected ? "#ecfdf5" : "#ffffff",
  mb: 0.75,
  transition: "all 0.15s ease",
  "&:hover": { borderColor: "#6ee7b7", bgcolor: "#f0fdf4" },
  "& .MuiFormControlLabel-label": {
    fontSize: "0.76rem",
    color: selected ? "#065f46" : "#374151",
    fontWeight: selected ? 600 : 400,
  },
});

const checkboxOptionSx = (selected) => ({
  m: 0,
  px: 1.5,
  py: 0.75,
  borderRadius: "10px",
  border: `1px solid ${selected ? "#059669" : "#e5e7eb"}`,
  bgcolor: selected ? "#ecfdf5" : "#ffffff",
  mb: 0.75,
  transition: "all 0.15s ease",
  "&:hover": { borderColor: "#6ee7b7", bgcolor: "#f0fdf4" },
  "& .MuiFormControlLabel-label": {
    fontSize: "0.76rem",
    color: selected ? "#065f46" : "#374151",
    fontWeight: selected ? 600 : 400,
  },
});

const radioSx = {
  color: "#d1d5db",
  p: 0.5,
  "&.Mui-checked": { color: "#059669" },
};

const checkboxSx = {
  color: "#d1d5db",
  p: 0.5,
  "&.Mui-checked": { color: "#059669" },
};

const submitBtnSx = {
  borderRadius: "10px",
  py: 1.1,
  background: "linear-gradient(135deg, #065f46 0%, #059669 100%)",
  fontWeight: 700,
  fontSize: "0.78rem",
  textTransform: "none",
  boxShadow: "0 4px 14px rgba(5,150,105,0.3)",
  "&:hover": { background: "linear-gradient(135deg, #064e3b 0%, #047857 100%)" },
  "&.Mui-disabled": { background: "#e5e7eb", color: "#9ca3af", boxShadow: "none" },
};

const selectSx = {
  fontSize: "0.76rem",
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#059669", borderWidth: "1px" },
};

// ── Program options ───────────────────────────────────────
const PROGRAMS = [
  {
    value: "irp",
    label: "EduSkills Industry Ready Program (IRP)",
    tagline: "Learn. Build. Become Industry Ready.",
    description:
      "A 3-day industry-oriented, hands-on student development program designed to equip learners with practical skills, real-world exposure, and job readiness through expert-led sessions and live projects.",
    icon: "🎓",
  },
  {
    value: "hackathon",
    label: "EduSkills Campus Hackathon",
    tagline: "Build. Innovate. Compete.",
    description:
      "A 3-day, high-energy innovation challenge designed to bring together students to solve real-world problems through technology. Participants collaborate in teams, work on relevant problem statements, and build functional prototypes through hands-on development and peer learning.",
    icon: "💡",
  },
  {
    value: "techcamp",
    label: "Tech Camp",
    tagline: "Skills. Projects. Growth.",
    description:
      "An immersive technical training camp with domain-specific curriculum, hands-on projects, and industry mentors.",
    icon: "🏕️",
  },
];

// ── Sub-components ────────────────────────────────────────
function StepCard({ question, hint, children }) {
  return (
    <Box>
      <Box sx={{ p: 2, borderRadius: "14px", border: "1px solid #e5e7eb", bgcolor: "#fafafa" }}>
        <Typography sx={{ fontSize: "0.78rem", color: "#111827", fontWeight: 600, lineHeight: 1.55, mb: hint ? 0.5 : 1.5 }}>
          {question}
        </Typography>
        {hint && (
          <Typography sx={{ fontSize: "0.67rem", color: "#6b7280", mb: 1.25 }}>
            {hint}
          </Typography>
        )}
        {children}
      </Box>
    </Box>
  );
}

function SuccessScreen({ onClose }) {
  return (
    <Box sx={{ textAlign: "center", py: 4, px: 3 }}>
      <CheckCircleIcon sx={{ fontSize: 52, color: "#22c55e", mb: 1.5 }} />
      <Typography sx={{ fontWeight: 800, fontSize: "0.98rem", color: "#111827", mb: 0.75 }}>
        Thank you for your response!
      </Typography>
      <Typography sx={{ fontSize: "0.75rem", color: "#6b7280", lineHeight: 1.7, mb: 2.5 }}>
        Our team will connect with you with further details and next steps.
      </Typography>
      <Button onClick={onClose} variant="contained" sx={{ ...submitBtnSx, px: 3, py: 1 }}>
        Close
      </Button>
    </Box>
  );
}

// ── Main Component ────────────────────────────────────────
function TechCampHostingSurveyPopup({ open, onClose, userRole }) {

  // ── ALL useState hooks grouped at the top ──────────────
  const [loading, setLoading]               = useState(false);
  const [submitted, setSubmitted]           = useState(false);
  const [apiError, setApiError]             = useState("");
  const [step, setStep]                     = useState(1);
  const [statusChecked, setStatusChecked]   = useState(false);
  const [q1, setQ1]                         = useState("");
  const [q1Reason, setQ1Reason]             = useState("");
  const [q2Programs, setQ2Programs]         = useState([]);
  const [q3StudentCount, setQ3StudentCount] = useState("");
  const [q3Month, setQ3Month]               = useState("");
  const [q4Suggestions, setQ4Suggestions]   = useState("");

  const isAllowedRole = ALLOWED_ROLES.includes(userRole?.toLowerCase());

  // ── ALL useEffect hooks grouped after useState ─────────

  // Role guard effect
  useEffect(() => {
    if (open && !isAllowedRole) {
      onClose();
    }
  }, [open, userRole]);

  // Status check effect
  useEffect(() => {
    if (!open || !isAllowedRole) return;

    let cancelled = false;

    const checkStatus = async () => {
      try {
        const res = await api.get("/api/tech-camp-hosting-survey/status");
        if (cancelled) return;
        if (res.data?.has_submitted) {
          onClose();
          return;
        }
      } catch (err) {
        if (cancelled) return;
        console.warn("Survey status check failed:", err?.response?.status);
      } finally {
        if (!cancelled) setStatusChecked(true);
      }
    };

    setStatusChecked(false);
    checkStatus();

    return () => { cancelled = true; };
  }, [open]);

  // ── Role guard render block — AFTER all hooks ──────────
  if (!isAllowedRole) return null;

  // ── Step logic ──────────────────────────────────────────
  const isEarlyExit = q1 === "no" || q1 === "maybe";
  const isFinalStep = isEarlyExit ? step === 1 : step === 4;

  const stepValid = () => {
    if (step === 1) {
      if (q1 === "no" || q1 === "maybe") return !!q1Reason.trim();
      return !!q1;
    }
    if (step === 2) return q2Programs.length > 0;
    if (step === 3) return !!q3StudentCount && !!q3Month;
    if (step === 4) return true;
    return true;
  };

  const showBottomNav = !isEarlyExit;

  const handleNext = () => { setApiError(""); setStep((s) => s + 1); };
  const handleBack = () => { setApiError(""); setStep((s) => s - 1); };

  const toggleProgram = (val) => {
    setQ2Programs((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  };

  // ── Submit ──────────────────────────────────────────────
  const handleSubmit = async () => {
    setLoading(true);
    setApiError("");

    const payload = {
      q1_interest: q1,
      q1_reason:   q1Reason || null,
      ...(q1 === "yes" && {
        q2_programs:      q2Programs,
        q3_student_count: q3StudentCount,
        q3_month:         q3Month,
        q4_suggestions:   q4Suggestions || null,
      }),
    };

    try {
      await api.post("/api/tech-camp-hosting-survey/submit", payload);
      setSubmitted(true);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409)      setApiError("You have already submitted a response.");
      else if (status === 422) setApiError(typeof detail === "string" ? detail : "Please complete all required fields.");
      else if (status === 401) setApiError("Session expired. Please log in again.");
      else                     setApiError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setSubmitted(false); setApiError(""); setStep(1);
      setStatusChecked(false);
      setQ1(""); setQ1Reason("");
      setQ2Programs([]);
      setQ3StudentCount(""); setQ3Month("");
      setQ4Suggestions("");
    }, 300);
  };

  // ── Inline submit (for No / Maybe) ─────────────────────
  const InlineSubmit = () => (
    <Button
      fullWidth variant="contained"
      disabled={!stepValid() || loading}
      onClick={handleSubmit}
      sx={{ ...submitBtnSx, mt: 1.5 }}
    >
      {loading ? <CircularProgress size={18} sx={{ color: "white" }} /> : "Submit Response 🚀"}
    </Button>
  );

  // ── Render steps ────────────────────────────────────────
  const renderStep = () => {

    if (step === 1)
      return (
        <StepCard question="Are you interested in hosting / participating in the 3-day program?">
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={q1}
              onChange={(e) => { setQ1(e.target.value); setQ1Reason(""); setApiError(""); }}
            >
              {[
                { value: "yes", label: "Yes" },
                { value: "no",  label: "No" },
              ].map((opt) => (
                <FormControlLabel
                  key={opt.value} value={opt.value}
                  control={<Radio size="small" sx={radioSx} />}
                  label={opt.label}
                  sx={optionSx(q1 === opt.value)}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {(q1 === "no" || q1 === "maybe") && (
            <>
              <Box sx={{ mt: 1.25, p: 1.5, borderRadius: "10px", bgcolor: "#fff7ed", border: "1px solid #fed7aa" }}>
                <Typography sx={{ fontSize: "0.67rem", color: "#92400e", fontWeight: 700, mb: 0.75 }}>
                  ✍️ {q1 === "no" ? "Why are you not interested?" : "What details do you need?"}
                  <Box component="span" sx={{ color: "#dc2626", ml: 0.3 }}>*</Box>
                </Typography>
                <TextField
                  fullWidth multiline minRows={2}
                  placeholder="Please share your reason..."
                  value={q1Reason}
                  onChange={(e) => setQ1Reason(e.target.value)}
                  size="small"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      fontSize: "0.75rem", borderRadius: "8px", bgcolor: "#ffffff",
                      "& fieldset": { borderColor: "#fed7aa" },
                      "&:hover fieldset": { borderColor: "#fb923c" },
                      "&.Mui-focused fieldset": { borderColor: "#f97316", borderWidth: "1px" },
                    },
                  }}
                />
              </Box>
              <Box sx={{ mt: 1, p: 1.25, borderRadius: "10px", bgcolor: "#fef2f2", border: "1px solid #fecaca" }}>
                <Typography sx={{ fontSize: "0.67rem", color: "#991b1b", lineHeight: 1.6 }}>
                  ⚠️ Reason is required to submit your response.
                </Typography>
              </Box>
              <InlineSubmit />
            </>
          )}
        </StepCard>
      );

    if (step === 2)
      return (
        <StepCard
          question="Which program(s) are you interested in hosting?"
          hint="You may select one or more programs"
        >
          <FormGroup>
            {PROGRAMS.map((prog) => {
              const selected = q2Programs.includes(prog.value);
              return (
                <Box
                  key={prog.value}
                  onClick={() => toggleProgram(prog.value)}
                  sx={{
                    ...checkboxOptionSx(selected),
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 0,
                  }}
                >
                  <Checkbox
                    checked={selected}
                    size="small"
                    sx={{ ...checkboxSx, mt: 0.1, flexShrink: 0 }}
                    onChange={() => toggleProgram(prog.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <Box sx={{ flex: 1, pt: 0.25 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.3 }}>
                      <Typography sx={{ fontSize: "0.82rem" }}>{prog.icon}</Typography>
                      <Typography sx={{ fontSize: "0.76rem", fontWeight: 700, color: selected ? "#065f46" : "#111827" }}>
                        {prog.label}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "0.65rem", color: selected ? "#059669" : "#6b7280", fontStyle: "italic", mb: 0.4, fontWeight: selected ? 600 : 400 }}>
                      {prog.tagline}
                    </Typography>
                    <Typography sx={{ fontSize: "0.67rem", color: "#6b7280", lineHeight: 1.5 }}>
                      {prog.description}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </FormGroup>
        </StepCard>
      );

    if (step === 3)
      return (
        <StepCard question="Scheduling & Participation Details" hint="Please fill in both fields">
          <Typography sx={{ fontSize: "0.7rem", color: "#374151", fontWeight: 600, mb: 0.75 }}>
            Expected Number of Students <Box component="span" sx={{ color: "#dc2626" }}>*</Box>
          </Typography>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <Select
              value={q3StudentCount}
              onChange={(e) => setQ3StudentCount(e.target.value)}
              displayEmpty
              sx={selectSx}
              renderValue={(val) => val || <Typography sx={{ fontSize: "0.76rem", color: "#9ca3af" }}>Select a range</Typography>}
            >
              <MenuItem value="50–100"  sx={{ fontSize: "0.76rem" }}>50–100</MenuItem>
              <MenuItem value="100–200" sx={{ fontSize: "0.76rem" }}>100–200</MenuItem>
              <MenuItem value="200+"    sx={{ fontSize: "0.76rem" }}>200+</MenuItem>
            </Select>
          </FormControl>

          <Typography sx={{ fontSize: "0.7rem", color: "#374151", fontWeight: 600, mb: 0.75 }}>
            Preferred Month <Box component="span" sx={{ color: "#dc2626" }}>*</Box>
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={q3Month}
              onChange={(e) => setQ3Month(e.target.value)}
              displayEmpty
              sx={selectSx}
              renderValue={(val) => val || <Typography sx={{ fontSize: "0.76rem", color: "#9ca3af" }}>Select a month range</Typography>}
            >
              <MenuItem value="May–June"          sx={{ fontSize: "0.76rem" }}>May–June</MenuItem>
              <MenuItem value="July–August"        sx={{ fontSize: "0.76rem" }}>July–August</MenuItem>
              <MenuItem value="September–October"  sx={{ fontSize: "0.76rem" }}>September–October</MenuItem>
              <MenuItem value="October–November"   sx={{ fontSize: "0.76rem" }}>October–November</MenuItem>
              <MenuItem value="November–December"  sx={{ fontSize: "0.76rem" }}>November–December</MenuItem>
              <MenuItem value="Flexible"           sx={{ fontSize: "0.76rem" }}>Flexible</MenuItem>
            </Select>
          </FormControl>
        </StepCard>
      );

    if (step === 4)
      return (
        <StepCard
          question="Any specific expectations or suggestions?"
          hint="Optional – Share any specific needs, expectations, or requirements."
        >
          <TextField
            fullWidth multiline minRows={4} size="small"
            placeholder="Any additional suggestions, special requirements, or expectations..."
            value={q4Suggestions}
            onChange={(e) => setQ4Suggestions(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "0.76rem", borderRadius: "10px",
                "& fieldset": { borderColor: "#e5e7eb" },
                "&:hover fieldset": { borderColor: "#d1d5db" },
                "&.Mui-focused fieldset": { borderColor: "#059669", borderWidth: "1px" },
              },
            }}
          />
          <Box
            sx={{
              mt: 2, p: 1.5, borderRadius: "10px",
              background: "linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)",
              border: "1px solid #a7f3d0",
            }}
          >
            <Typography sx={{ fontSize: "0.67rem", color: "#047857", lineHeight: 1.6 }}>
              Thank you for your response. Our team will connect with you with further details and next steps.
            </Typography>
          </Box>
        </StepCard>
      );
  };

  // ── Show a loader while the status check is in-flight ──
  if (open && !statusChecked && !submitted) {
    return (
      <Dialog open={open} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
        <DialogContent>
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 5 }}>
            <CircularProgress size={28} sx={{ color: "#059669" }} />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (submitted) return;
        if (reason === "backdropClick") return;
        handleClose();
      }}
      maxWidth="xs"
      fullWidth
      disableEscapeKeyDown={submitted}
      PaperProps={{
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
          maxHeight: "92vh",
        },
      }}
    >
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", overflow: "auto" }}>

        {/* ── Hero Banner ── */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #052e16 0%, #064e3b 55%, #059669 100%)",
            px: 3, pt: 3, pb: 2.5,
            position: "relative", overflow: "hidden", flexShrink: 0,
          }}
        >
          <Box sx={{ position: "absolute", top: -35, right: -35, width: 130, height: 130, borderRadius: "50%", background: "rgba(255,255,255,0.05)", pointerEvents: "none" }} />
          <Box sx={{ position: "absolute", bottom: -25, left: -25, width: 95, height: 95, borderRadius: "50%", background: "rgba(255,255,255,0.04)", pointerEvents: "none" }} />

          <Chip
            label="🏕️ Hosting Interest Survey"
            size="small"
            sx={{
              bgcolor: "rgba(255,255,255,0.12)", color: "#6ee7b7",
              fontWeight: 700, fontSize: "0.65rem", letterSpacing: "0.04em",
              mb: 1.5, border: "1px solid rgba(110,231,183,0.25)", height: "22px",
            }}
          />

          <Typography sx={{ color: "#ffffff", fontWeight: 800, lineHeight: 1.25, fontSize: "1.1rem", mb: 1.5 }}>
            Survey Form: 3-Day Student Development Program
          </Typography>
        </Box>

        {/* ── Body ── */}
        <Box sx={{ px: 2.5, pt: 2, pb: 2.5, bgcolor: "#ffffff", flexShrink: 0 }}>
          {submitted ? (
            <SuccessScreen onClose={handleClose} />
          ) : (
            <>
              {renderStep()}

              {apiError && (
                <Alert severity="error" onClose={() => setApiError("")} sx={{ mt: 1.5, fontSize: "0.72rem", borderRadius: "10px", py: 0.5 }}>
                  {apiError}
                </Alert>
              )}

              {showBottomNav && (
                <Box display="flex" gap={1} mt={2}>
                  {step > 1 && (
                    <Button
                      variant="outlined" onClick={handleBack}
                      startIcon={<ArrowBackIcon />}
                      sx={{
                        borderRadius: "10px", py: 1.1, px: 2,
                        fontWeight: 600, fontSize: "0.75rem", textTransform: "none",
                        borderColor: "#e5e7eb", color: "#374151",
                        "&:hover": { borderColor: "#d1d5db", bgcolor: "#f9fafb" },
                        flexShrink: 0,
                      }}
                    >
                      Back
                    </Button>
                  )}
                  {isFinalStep ? (
                    <Button
                      fullWidth variant="contained"
                      disabled={!stepValid() || loading}
                      onClick={handleSubmit}
                      sx={submitBtnSx}
                    >
                      {loading ? <CircularProgress size={18} sx={{ color: "white" }} /> : "Submit Response 🚀"}
                    </Button>
                  ) : (
                    <Button
                      fullWidth variant="contained"
                      disabled={!stepValid()}
                      onClick={handleNext}
                      sx={submitBtnSx}
                    >
                      Next →
                    </Button>
                  )}
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default TechCampHostingSurveyPopup;